import { inject, Injectable } from '@angular/core';
import { CacheService } from '../services/cache.service';
import { ApiService } from './base-api/api.service';
import { environment } from '../../../environments/environment';
import { map, Observable } from 'rxjs';
import { Country } from '../models/country';
import { ApplicationStatus } from '../models/application-status';
import { DocumentTypeFormat } from '../models/document-type-format';
import { DocumentFormat } from '../models/document-format';
import { FormFieldCategory } from '../models/form-field-category';
import { FormFieldType } from '../models/form-field-type';
import { GradePointAverage } from '../models/grade-point-average';
import { Requirement } from '../models/requirement';
import { TemplateField } from '../models/template-field';
import { DocumentType } from '../models/document-type';
import { AllocationFrequency } from '../models/allocation-frequency';
import { ScholarshipCategory } from '../models/scholarship-category';
import { ScholarshipStatus } from '../models/scholarship-status';
import { Eligibility } from '../models/eligibility';
import { ScholarshipProvider } from '../models/scholarship-provider';
import { Institution } from '../models/institution';
import { Course } from '../models/course';
import { IdentityType } from '../models/identity-type';
import { Role } from '../models/role';

@Injectable({
  providedIn: 'root',
})
export class LookupsApiService {
  private readonly cache = inject(CacheService);
  private readonly api = inject(ApiService);

  getIdentityTypes(): Observable<IdentityType[]> {
    return this.cache.wrap(
      'identity-types',
      this.api.get<{ data: IdentityType[] }>('identity-types').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getInstitutions(): Observable<Institution[]> {
    return this.cache.wrap(
      'institutions',
      this.api.get<{ data: Institution[] }>('institutions').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getRoles(): Observable<Role[]> {
    return this.cache.wrap(
      'roles',
      this.api.get<{ data: Role[] }>('roles').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getCountries(): Observable<Country[]> {
    return this.cache.wrap(
      'countries-list',
      this.api.get<{ data: Country[] }>('countries').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getApplicationStatuses(): Observable<ApplicationStatus[]> {
    return this.cache.wrap(
      'application-statuses',
      this.api.get<{ data: ApplicationStatus[] }>('application-statuses').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getDocumentTypes(): Observable<DocumentType[]> {
    return this.cache.wrap(
      'document-types',
      this.api.get<{ data: DocumentType[] }>('document-types').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getDocumentTypeFormats(): Observable<DocumentTypeFormat[]> {
    return this.cache.wrap(
      'document-type-formats',
      this.api.get<{ data: DocumentTypeFormat[] }>('document-type-formats').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getDocumentFormats(): Observable<DocumentFormat[]> {
    return this.cache.wrap(
      'document-formats',
      this.api.get<{ data: DocumentFormat[] }>('document-formats').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getFormFieldCategories(): Observable<FormFieldCategory[]> {
    return this.cache.wrap(
      'form-field-categories',
      this.api.get<{ data: FormFieldCategory[] }>('form-field-categories').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getFormFieldTypes(): Observable<FormFieldType[]> {
    return this.cache.wrap(
      'form-field-types',
      this.api.get<{ data: FormFieldType[] }>('form-field-types').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getGradePointAverages(): Observable<GradePointAverage[]> {
    return this.cache.wrap(
      'grade-point-averages',
      this.api.get<{ data: GradePointAverage[] }>('grade-point-averages').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getScholarshipRequirements(): Observable<Requirement[]> {
    return this.cache.wrap(
      'scholarship-requirements',
      this.api.get<{ data: Requirement[] }>('scholarship-requirements').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getTemplateFields(): Observable<TemplateField[]> {
    return this.cache.wrap(
      'template-fields',
      this.api.get<{ data: TemplateField[] }>('template-fields').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getAllocationFrequencies(): Observable<AllocationFrequency[]> {
    return this.cache.wrap(
      'allocation-frequencies',
      this.api.get<{ data: AllocationFrequency[] }>('allocation-frequencies').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getScholarshipCategories(): Observable<ScholarshipCategory[]> {
    return this.cache.wrap(
      'scholarship-categories',
      this.api.get<{ data: ScholarshipCategory[] }>('scholarship-categories').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getScholarshipStatuses(): Observable<ScholarshipStatus[]> {
    return this.cache.wrap(
      'scholarship-statuses',
      this.api.get<{ data: ScholarshipStatus[] }>('scholarship-statuses').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getEligibilties(): Observable<Eligibility[]> {
    return this.cache.wrap(
      'eligibilities',
      this.api.get<{ data: Eligibility[] }>('eligibilities').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getScholarshipProviders(): Observable<ScholarshipProvider[]> {
    return this.cache.wrap(
      'scholarship-providers',
      this.api.get<{ data: ScholarshipProvider[] }>('scholarship-providers').pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getCountryInstitutions(countryId: number): Observable<Institution[]> {
    return this.cache.wrap(
      `institutions-${countryId}`,
      this.api.get<{ data: any[] }>(`countries/${countryId}/institutions`).pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getInstitutionCourses(institutionId: number): Observable<Course[]> {
    return this.cache.wrap(
      `courses-${institutionId}`,
      this.api.get<{ data: any[] }>(`institutions/${institutionId}/courses`).pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }
}
